// src/components/Administration/DeviceList/ImportDevicesModal.js
import React, { useState } from 'react';
import {
    Modal,
    Upload,
    Divider,
    Space,
    Button,
    Popconfirm,
    message,
    Collapse,
    Tree,
    Typography,
    Badge,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';

const { Panel } = Collapse;
const { Text } = Typography;

/**
 * Convert any JS value into AntD Tree nodes.
 * - Arrays become a node with index children.
 * - Objects become a node with key children.
 * - Primitives become leaf nodes with their string value.
 */
const toTreeData = (value, keyPrefix = 'root') => {
    const makeLeaf = (k, v) => ({
        key: `${keyPrefix}-${k}`,
        title: (
            <span>
        <Text strong>{k}:</Text> <Text type="secondary">{String(v)}</Text>
      </span>
        ),
        isLeaf: true,
    });

    if (Array.isArray(value)) {
        // array children: index nodes
        const children = value.map((item, idx) => {
            const childKey = `${keyPrefix}-idx-${idx}`;
            if (item !== null && typeof item === 'object') {
                return {
                    key: childKey,
                    title: <Text>[{idx}]</Text>,
                    children: toTreeData(item, childKey),
                };
            }
            return {
                key: childKey,
                title: (
                    <span>
            <Text strong>[{idx}]:</Text>{' '}
                        <Text type="secondary">{String(item)}</Text>
          </span>
                ),
                isLeaf: true,
            };
        });
        return children;
    }

    if (value && typeof value === 'object') {
        const entries = Object.entries(value);
        return entries.map(([k, v]) => {
            const childKey = `${keyPrefix}-${k}`;
            if (v !== null && typeof v === 'object') {
                return {
                    key: childKey,
                    title: <Text>{k}</Text>,
                    children: toTreeData(v, childKey),
                };
            }
            return makeLeaf(k, v);
        });
    }

    // primitive root
    return [
        {
            key: `${keyPrefix}-value`,
            title: <Text type="secondary">{String(value)}</Text>,
            isLeaf: true,
        },
    ];
};

/**
 * ImportDevicesModal
 * - Allows selecting a JSON export bundle and previewing it
 * - Calls onImport({ bundle }) when the user confirms
 *   bundle can be:
 *   { devices: [...] }  (simple mode)
 *   or the full bundle { dsync, clients, events, deviceConfigs, devices }
 */
const ImportDevicesModal = ({ open, onClose, onImport, loading = false }) => {
    const [preview, setPreview] = useState(null);

    const handleBeforeUpload = (file) => {
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const parsed = JSON.parse(reader.result);

                const isBundle =
                    parsed &&
                    (Array.isArray(parsed.devices) ||
                        parsed.dsync ||
                        parsed.clients ||
                        parsed.events ||
                        parsed.deviceConfigs);

                if (!isBundle) {
                    message.error(
                        'Invalid JSON. Expected { devices: [...] } or a bundle with { dsync, clients, events, deviceConfigs, devices }.'
                    );
                    return;
                }

                const devices = Array.isArray(parsed.devices) ? parsed.devices : [];
                const clients = Array.isArray(parsed.clients) ? parsed.clients : [];
                const events = Array.isArray(parsed.events) ? parsed.events : [];
                const deviceConfigs = Array.isArray(parsed.deviceConfigs) ? parsed.deviceConfigs : [];

                setPreview({
                    counts: {
                        devices: devices.length,
                        clients: clients.length,
                        events: events.length,
                        deviceConfigs: deviceConfigs.length,
                    },
                    sample: devices.slice(0, 3),
                    raw: parsed,
                });

                message.success(
                    `Bundle loaded. Devices: ${devices.length}, Clients: ${clients.length}, Events: ${events.length}, DeviceConfigs: ${deviceConfigs.length}`
                );
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);
                message.error('The JSON could not be parsed.');
            }
        };
        reader.readAsText(file);
        // prevent automatic upload
        return false;
    };

    const handleConfirmImport = async () => {
        if (!preview?.raw) return;
        await onImport?.({ bundle: preview.raw });
        setPreview(null);
    };

    const handleClose = () => {
        setPreview(null); // keep this behavior: closing modal clears preview
        onClose?.();
    };

    // Helpers for panel headers with counters
    const headerWithCount = (label, count) => (
        <Space>
            <Text>{label}</Text>
            <Badge count={count} overflowCount={9999} />
        </Space>
    );

    const raw = preview?.raw || {};
    const devices = Array.isArray(raw.devices) ? raw.devices : [];
    const clients = Array.isArray(raw.clients) ? raw.clients : [];
    const events = Array.isArray(raw.events) ? raw.events : [];
    const deviceConfigs = Array.isArray(raw.deviceConfigs) ? raw.deviceConfigs : [];

    return (
        <Modal
            title="Import devices"
            open={open}
            onCancel={handleClose}
            footer={null}
            destroyOnClose
            width={900}
        >
            {/* Step 1 - Upload JSON */}
            <Upload.Dragger
                name="file"
                multiple={false}
                accept=".json,application/json"
                beforeUpload={handleBeforeUpload}
            >
                <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                </p>
                <p className="ant-upload-text">Drag or click to select an exported .json file.</p>
                <p className="ant-upload-hint">
                    The import performs an <b>upsert by SerialNumber</b> and retries linking DeviceConfig by id/name.
                </p>
            </Upload.Dragger>

            {preview && (
                <>
                    <Divider />
                    <p>
                        <b>Summary:</b>{' '}
                        {preview.counts?.devices ?? 0} devices,{' '}
                        {preview.counts?.clients ?? 0} clients,{' '}
                        {preview.counts?.events ?? 0} events,{' '}
                        {preview.counts?.deviceConfigs ?? 0} configs
                    </p>

                    <Collapse
                        accordion={false}
                        bordered
                        defaultActiveKey={['devices', 'clients', 'events', 'configs']}
                    >
                        <Panel header={headerWithCount('Devices', devices.length)} key="devices">
                            {devices.length ? (
                                <Tree
                                    defaultExpandAll={false}
                                    treeData={[
                                        {
                                            key: 'devices-root',
                                            title: <Text strong>devices</Text>,
                                            children: toTreeData(devices, 'devices'),
                                        },
                                    ]}
                                />
                            ) : (
                                <Text type="secondary">No devices in this bundle.</Text>
                            )}
                        </Panel>

                        <Panel header={headerWithCount('Clients', clients.length)} key="clients">
                            {clients.length ? (
                                <Tree
                                    defaultExpandAll={false}
                                    treeData={[
                                        {
                                            key: 'clients-root',
                                            title: <Text strong>clients</Text>,
                                            children: toTreeData(clients, 'clients'),
                                        },
                                    ]}
                                />
                            ) : (
                                <Text type="secondary">No clients in this bundle.</Text>
                            )}
                        </Panel>

                        <Panel header={headerWithCount('Events', events.length)} key="events">
                            {events.length ? (
                                <Tree
                                    defaultExpandAll={false}
                                    treeData={[
                                        {
                                            key: 'events-root',
                                            title: <Text strong>events</Text>,
                                            children: toTreeData(events, 'events'),
                                        },
                                    ]}
                                />
                            ) : (
                                <Text type="secondary">No events in this bundle.</Text>
                            )}
                        </Panel>

                        <Panel header={headerWithCount('DeviceConfigs', deviceConfigs.length)} key="configs">
                            {deviceConfigs.length ? (
                                <Tree
                                    defaultExpandAll={false}
                                    treeData={[
                                        {
                                            key: 'configs-root',
                                            title: <Text strong>deviceConfigs</Text>,
                                            children: toTreeData(deviceConfigs, 'deviceConfigs'),
                                        },
                                    ]}
                                />
                            ) : (
                                <Text type="secondary">No device configs in this bundle.</Text>
                            )}
                        </Panel>

                        <Panel header="Raw JSON" key="raw">
              <pre
                  style={{
                      maxHeight: 260,
                      overflow: 'auto',
                      background: '#11182711',
                      padding: 8,
                      borderRadius: 6,
                  }}
              >
{JSON.stringify(raw, null, 2)}
              </pre>
                        </Panel>
                    </Collapse>

                    <Divider />
                    <Space>
                        <Popconfirm
                            title="Import now?"
                            description="Devices will be created/updated by SerialNumber. Continue?"
                            okText="Import"
                            cancelText="Cancel"
                            onConfirm={handleConfirmImport}
                        >
                            <Button type="primary" loading={loading}>
                                Import
                            </Button>
                        </Popconfirm>
                        <Button onClick={() => setPreview(null)}>Clear</Button>
                    </Space>
                </>
            )}
        </Modal>
    );
};

export default ImportDevicesModal;